// frontend/src/services/authService.tsx
import { User } from '../models/User';
import { UserService } from './userService';
import showErrorToast from '../components/Toasts/ShowErrorToast';
import showSuccessToast from '../components/Toasts/ShowSuccessToast';

export interface TokenInfo {
    token: string;
    expiresAt: number;
    issuedAt: number;
}

export class AuthService {
    private static readonly TOKEN_KEY = 'gpcwstoken_kw';
    private static readonly ROLE_KEY = 'gpcwstoken_kw_role';
    private static readonly TOKEN_INFO_KEY = 'gpcwstoken_info';
    private static readonly WARNING_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiration
    private static readonly REFRESH_THRESHOLD = 10 * 60 * 1000; // 10 minutes before expiration
    private static monitoringInterval: ReturnType<typeof setInterval> | null = null;
    private static tokenExpiredListenerAttached = false;

    /**
     * Store token with expiration information
     */
    static storeToken(token: string, expiresIn: number = 10 * 60 * 60 * 1000): void {
        const now = Date.now();
        const expiresAt = now + expiresIn;

        const tokenInfo: TokenInfo = {
            token,
            expiresAt,
            issuedAt: now
        };

        localStorage.setItem(this.TOKEN_KEY, token);
        localStorage.setItem(this.TOKEN_INFO_KEY, JSON.stringify(tokenInfo));
    }

    /**
     * Get stored token
     */
    static getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY);
    }

    /**
     * Get token information
     */
    static getTokenInfo(): TokenInfo | null {
        const info = localStorage.getItem(this.TOKEN_INFO_KEY);
        return info ? JSON.parse(info) : null;
    }

    /**
     * Check if token is expired
     */
    static isTokenExpired(): boolean {
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo) return true;

        return Date.now() >= tokenInfo.expiresAt;
    }

    /**
     * Check if token will expire soon (within warning threshold)
     */
    static isTokenExpiringSoon(): boolean {
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo) return true;

        const timeUntilExpiry = tokenInfo.expiresAt - Date.now();
        return timeUntilExpiry <= this.WARNING_THRESHOLD;
    }

    /**
     * Check if token should be refreshed (within refresh threshold)
     */
    static shouldRefreshToken(): boolean {
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo) return false;

        const timeUntilExpiry = tokenInfo.expiresAt - Date.now();
        return timeUntilExpiry <= this.REFRESH_THRESHOLD;
    }

    /**
     * Get time until token expires in minutes
     */
    static getTimeUntilExpiry(): number {
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo) return 0;

        const timeUntilExpiry = tokenInfo.expiresAt - Date.now();
        return Math.max(0, Math.ceil(timeUntilExpiry / (60 * 1000)));
    }

    /**
     * Clear all authentication data
     */
    static clearAuth(): void {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.ROLE_KEY);
        localStorage.removeItem(this.TOKEN_INFO_KEY);

        if (this.monitoringInterval) {
            clearInterval(this.monitoringInterval);
            this.monitoringInterval = null;
        }
    }

    /**
     * Check if token can be extended
     */
    static canExtendToken(): boolean {
        const tokenInfo = this.getTokenInfo();
        if (!tokenInfo) return false;

        const timeUntilExpiry = tokenInfo.expiresAt - Date.now();
        // Can extend if token is not expired and has less than 30 minutes remaining
        return timeUntilExpiry > 0 && timeUntilExpiry <= (30 * 60 * 1000);
    }

    /**
     * Attempt to refresh the token by re-authenticating the user
     */
    static async attemptTokenRefresh(): Promise<boolean> {
        try {
            // Check if we have stored credentials or if we can extend the session
            const tokenInfo = this.getTokenInfo();

            if (!tokenInfo) {
                // No token info, can't refresh
                return false;
            }

            // Check if token is expired or very close to expiration
            const timeUntilExpiry = tokenInfo.expiresAt - Date.now();
            const isExpired = timeUntilExpiry <= 0;
            const isVeryCloseToExpiry = timeUntilExpiry <= (5 * 60 * 1000); // 5 minutes or less

            if (isExpired) {
                // Token is completely expired, can't refresh without re-authentication
                return false;
            }

            if (isVeryCloseToExpiry) {
                // Token is very close to expiration, extend it
                const newExpiresAt = Date.now() + (10 * 60 * 60 * 1000); // 10 hours from now
                const updatedTokenInfo: TokenInfo = {
                    ...tokenInfo,
                    expiresAt: newExpiresAt
                };

                localStorage.setItem(this.TOKEN_INFO_KEY, JSON.stringify(updatedTokenInfo));

                // Update the session time
                window.dispatchEvent(new CustomEvent('sessionExtended'));

                return true;
            }

            // Token still has reasonable time left, no need to extend yet
            return true;
        } catch (error) {
            console.error('Error attempting token refresh:', error);
            return false;
        }
    }

    /**
     * Show appropriate warning based on token status
     */
    static showTokenWarning(): void {
        const timeUntilExpiry = this.getTimeUntilExpiry();

        if (timeUntilExpiry === 0) {
            showErrorToast('Your session has expired. Please log in again.');
        } else if (timeUntilExpiry <= 5) {
            showErrorToast(`Your session will expire in ${timeUntilExpiry} minute${timeUntilExpiry > 1 ? 's' : ''}. Please save your work and log in again.`);
        } else if (timeUntilExpiry <= 10) {
            showErrorToast(`Your session will expire in ${timeUntilExpiry} minutes.`);
        }
    }

    /**
     * Initialize token monitoring
     */
    static initializeTokenMonitoring(): void {
        // Idempotent: called on every successful login and on every mount that finds a
        // valid token, so without this guard each call stacked another interval/listener,
        // causing duplicate "session expired" toasts/dialogs the more a tab logged in and out.
        if (this.monitoringInterval) {
            clearInterval(this.monitoringInterval);
        }

        // Check token status every minute
        this.monitoringInterval = setInterval(() => {
            if (this.isTokenExpired()) {
                // Token is expired, clear auth and notify
                this.clearAuth();
                window.dispatchEvent(new CustomEvent('tokenExpired'));
            } else if (this.isTokenExpiringSoon()) {
                // Token is expiring soon, show warning
                this.showTokenWarning();
            }
        }, 60 * 1000); // Check every minute

        if (!this.tokenExpiredListenerAttached) {
            this.tokenExpiredListenerAttached = true;
            // Listen for token expiration events
            window.addEventListener('tokenExpired', () => {
                this.clearAuth();
                showErrorToast('Your session has expired. Please log in again.');
            });
        }
    }

    /**
     * Login with automatic token management
     */
    static async login(username: string, password: string): Promise<boolean> {
        try {
            const success = await UserService.login(username, password);
            if (success) {
                // Initialize token monitoring after successful login
                this.initializeTokenMonitoring();
                showSuccessToast('Login successful!');
            }
            return success;
        } catch (error) {
            showErrorToast('Login failed. Please try again.');
            return false;
        }
    }

    /**
     * Logout and clear all data
     */
    static logout(): void {
        this.clearAuth();
        showSuccessToast('Logged out successfully');
    }
}
